import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { LeadForm } from '../components/LeadForm';
import { ReviewCard } from '../components/ReviewCard';
import { CoachCard } from '../components/CoachCard';
import { FAQAccordion } from '../components/FAQAccordion';
import { CTABand } from '../components/CTABand';
import { Shield, Target, Zap, Heart } from 'lucide-react';
export function StartFree() {
  useEffect(() => {
    document.title = 'Try Storm Taekwondo Free | Build Confidence & Focus';
  }, []);
  return <div className="flex flex-col" data-id="element-708">
      {/* Visual Hero */}
      <section className="relative min-h-[80vh] flex items-center pt-20 pb-24 overflow-hidden" data-id="element-709">
        <div className="absolute inset-0" data-id="element-710">
          <img src="https://images.unsplash.com/photo-1591117207239-788bf8de6c3b?auto=format&fit=crop&w=2000&q=80" alt="Kids smiling in martial arts class" className="w-full h-full object-cover" data-id="element-711" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/80 to-transparent" data-id="element-712" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-713">
          <div className="max-w-2xl" data-id="element-714">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-715">
              <div className="inline-block bg-storm-red text-white px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wide mb-6" data-id="element-716">
                Free One-Week Trial
              </div>
              <h1 className="text-5xl md:text-6xl text-white mb-6 leading-tight" data-id="element-717">
                Help Your Child Build Confidence - Try Storm Free
              </h1>
              <p className="text-xl text-off-white mb-10 leading-relaxed" data-id="element-718">
                Storm Taekwondo gives kids a structured, positive place to build
                discipline, focus, fitness and resilience. See the difference in
                just one week.
              </p>

              <div className="bg-paper p-6 rounded-2xl shadow-xl max-w-md" data-id="element-719">
                <h3 className="text-2xl text-ink mb-4 text-center" data-id="element-720">
                  Start Your Child's Free Week
                </h3>
                <LeadForm buttonText="Claim Free Week" data-id="element-721" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Emotional Benefits */}
      <section className="py-24 bg-off-white" data-id="element-722">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-723">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-724">
            <h2 className="text-4xl text-ink mb-6" data-id="element-725">Why Parents Choose Storm</h2>
            <p className="text-xl text-gray-warm" data-id="element-726">
              We teach more than just kicks and punches. We help parents build
              strong, resilient kids.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-727">
            {[{
            icon: Shield,
            title: 'Confidence',
            desc: 'To stand tall and speak up.'
          }, {
            icon: Target,
            title: 'Focus',
            desc: 'To listen and pay attention.'
          }, {
            icon: Zap,
            title: 'Discipline',
            desc: 'To do the right thing.'
          }, {
            icon: Heart,
            title: 'Resilience',
            desc: 'To bounce back from failure.'
          }].map((benefit, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="bg-paper p-8 rounded-2xl shadow-soft text-center border border-subtle-gray" data-id="element-728">
                <div className="w-16 h-16 bg-storm-red/10 rounded-full flex items-center justify-center mx-auto mb-6 text-storm-red" data-id="element-729">
                  <benefit.icon className="w-8 h-8" data-id="element-730" />
                </div>
                <h3 className="text-2xl text-ink mb-3" data-id="element-731">{benefit.title}</h3>
                <p className="text-gray-warm" data-id="element-732">{benefit.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* What the Free Week Includes */}
      <section className="py-24 bg-paper border-y border-subtle-gray" data-id="element-733">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-734">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-735">
            <div data-id="element-736">
              <img src="https://images.unsplash.com/photo-1555597408-26bc8e548a46?auto=format&fit=crop&w=1000&q=80" alt="Kids training" className="rounded-2xl shadow-xl" data-id="element-737" />
            </div>
            <div data-id="element-738">
              <h2 className="text-4xl text-ink mb-8" data-id="element-739">
                What Your Free Week Includes
              </h2>
              <div className="space-y-6" data-id="element-740">
                {['A personal welcome and introduction to the coaches', 'A full week of age-appropriate beginner classes', "An assessment of your child's goals and needs", 'A clear path forward if you choose to enroll'].map((item, idx) => <div key={idx} className="flex gap-4 items-start" data-id="element-741">
                    <div className="w-8 h-8 bg-storm-red rounded-full flex items-center justify-center shrink-0 text-white font-bold" data-id="element-742">
                      {idx + 1}
                    </div>
                    <p className="text-lg text-gray-warm pt-1" data-id="element-743">{item}</p>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-24 bg-off-white" data-id="element-744">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-745">
          <h2 className="text-4xl text-ink text-center mb-16" data-id="element-746">
            Real Parent Reviews
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-747">
            <ReviewCard quote="My son's confidence has skyrocketed since starting at Storm." name="Sarah M." label="Parent Review" data-id="element-748" />
            <ReviewCard quote="The coaches are incredibly patient and know exactly how to motivate the kids." name="David R." label="Parent Review" data-id="element-749" />
            <ReviewCard quote="Best decision we made for our daughter's focus and discipline." name="Jennifer L." label="Parent Review" data-id="element-750" />
            <ReviewCard quote="They teach real life skills, not just martial arts. Highly recommend." name="Marcus T." label="Parent Review" data-id="element-751" />
            <ReviewCard quote="A very welcoming and supportive environment for beginners." name="Amanda K." label="Parent Review" data-id="element-752" />
            <ReviewCard quote="My kids love going to class every week. It's their favorite activity." name="Brian C." label="Parent Review" data-id="element-753" />
          </div>
        </div>
      </section>

      {/* Coaches */}
      <section className="py-24 bg-paper border-t border-subtle-gray" data-id="element-754">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-755">
          <h2 className="text-4xl text-ink text-center mb-16" data-id="element-756">
            Meet the Coaches
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-757">
            <CoachCard name="Shane Merem" role="Owner / Team Storm Coach" bio="Instills grit, confidence, determination, and discipline in every student." imageUrl="https://images.unsplash.com/photo-1544117519-31a4b719223d?auto=format&fit=crop&w=600&q=80" data-id="element-758" />
            <CoachCard name="Megan Merem" role="Chief Instructor" bio="Two-time USA National Sparring Champion. Creates a friendly but firm learning environment." imageUrl="https://images.unsplash.com/photo-1591117207239-788bf8de6c3b?auto=format&fit=crop&w=600&q=80" data-id="element-759" />
            <CoachCard name="Michael Jacobs" role="Instructor" bio="USAT National Gold Medalist dedicated to student success." imageUrl="https://images.unsplash.com/photo-1555597673-b21d5c935865?auto=format&fit=crop&w=600&q=80" data-id="element-760" />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-off-white" data-id="element-761">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-762">
          <h2 className="text-4xl text-ink text-center mb-12" data-id="element-763">
            Common Questions
          </h2>
          <FAQAccordion faqs={[{
          question: 'What age can my child start?',
          answer: 'We accept students as young as 3 years old in our Mini Troopers program.'
        }, {
          question: 'Does my child need experience?',
          answer: 'No! Most of our students start as complete beginners.'
        }, {
          question: 'Do I need to buy a uniform for the trial?',
          answer: 'No gear is needed for the trial. Just wear comfortable athletic clothes.'
        }, {
          question: 'How do I schedule the first class?',
          answer: 'Fill out the form, and we will call or text you to find a time that works for your schedule.'
        }]} data-id="element-764" />
        </div>
      </section>

      <CTABand headline="Ready to See the Difference?" copy="Give your child the gift of confidence and focus. Claim your free week today." data-id="element-765" />
    </div>;
}